import { Box } from '@mui/material'
import AccessRequestPolicyDetails from 'components/AccessRequestPolicyDetails'
import { EndDateTimePicker } from 'components/TimeConstrainedControls/EndDateTimePicker'
import { StartDateTimePicker } from 'components/TimeConstrainedControls/StartDateTimePicker'
import { useAccessRequestPolicy } from 'hooks'
import moment from 'moment'
import { Icon } from 'packages/eid-icons'
import { ToggleSwitch } from 'packages/eid-ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    isDateInPast,
    isEndDateEditableForApprovalDecision,
    isStartDateEditableForApprovalDecision,
} from 'utils'

const DecisionTimeConstraintControlWrapper = ({
    originalStartDate,
    originalEndDate,
    requestPolicyId,
    selectedStartDate,
    selectedEndDate,
    handleStartDate,
    handleEndDate,
    handleToggleValue,
    timeConstraintRequired = false,
    approvalDecision,
}) => {
    const { t } = useTranslation()
    const policy = useAccessRequestPolicy(requestPolicyId)
    const [toggleValue, setToggle] = useState(
        Boolean(selectedEndDate) ||
            policy?.isTimeConstrained ||
            timeConstraintRequired,
    )

    useEffect(() => {
        if (!toggleValue) {
            handleEndDate(null)
            handleStartDate(originalStartDate)
        }
        handleToggleValue(toggleValue)
    }, [toggleValue])

    const handleToggle = () => {
        setToggle((prev) => !prev)
    }

    const startDateEditable =
        approvalDecision == null
            ? true
            : isStartDateEditableForApprovalDecision(approvalDecision)
    const endDateEditable =
        approvalDecision == null
            ? true
            : isEndDateEditableForApprovalDecision(approvalDecision)

    return (
        <Box display={'flex'} flexDirection={'row'}>
            <Box minWidth="14rem">
                <ToggleSwitch
                    value={toggleValue}
                    label={t('SetDuration')}
                    labelColor="#5d6870"
                    disabled={
                        policy?.isTimeConstrained || timeConstraintRequired
                    }
                    onChange={handleToggle}
                />
            </Box>

            {toggleValue && (
                <DecisionTimeConstraintControl
                    requestPolicyId={requestPolicyId}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={
                        selectedEndDate ? selectedEndDate : originalEndDate
                    }
                    handleStartDate={handleStartDate}
                    handleEndDate={handleEndDate}
                    timeConstraintRequired={timeConstraintRequired}
                    startDateEditable={startDateEditable}
                    endDateEditable={endDateEditable}
                />
            )}
        </Box>
    )
}

const DecisionTimeConstraintControl = ({
    requestPolicyId,
    selectedStartDate,
    selectedEndDate,
    handleStartDate,
    handleEndDate,
    startDateEditable = true,
    endDateEditable = true,
}) => {
    const policy = useAccessRequestPolicy(requestPolicyId)

    const getAccessLength = () => {
        let minuteDifference =
            selectedEndDate && selectedStartDate
                ? moment(selectedEndDate).diff(
                      moment(selectedStartDate),
                      'minutes',
                  )
                : 0

        const defaultMinutes = policy?.defaultValueInMinutes

        if (policy?.isTimeConstrained) {
            minuteDifference =
                minuteDifference > 0
                    ? Math.min(defaultMinutes, minuteDifference)
                    : defaultMinutes
        } else {
            minuteDifference =
                minuteDifference > 0 ? minuteDifference : defaultMinutes
        }
        return minuteDifference
    }

    const handleSelectedStartDate = () => {
        if (isDateInPast(selectedStartDate) || !selectedStartDate) {
            const updatedDate = moment().format()
            handleStartDate(updatedDate)
            return
        }
        return
    }

    const handleSelectedEndDate = () => {
        if (!selectedStartDate || !policy?.defaultValueInMinutes) {
            return ''
        }
        const updatedDate = moment(selectedStartDate)
            .add(getAccessLength(), 'minute')
            .format()
        handleEndDate(updatedDate)
        return
    }

    useEffect(() => {
        handleSelectedStartDate()
    }, [])

    const isInvalidDate = moment(selectedStartDate).isAfter(
        moment(selectedEndDate),
    )

    useEffect(() => {
        if (!selectedEndDate) {
            handleSelectedEndDate()
        }
    }, [selectedStartDate])

    return (
        <Box display={'flex'} justifyContent={'space-between'}>
            <Box display={'flex'}>
                <>
                    <Box minWidth={'20rem'}>
                        <Box padding={'0 0.4rem'} display="flex">
                            <Box
                                style={{
                                    alignSelf: 'end',
                                    marginRight: '0.8rem',
                                }}
                            >
                                <Icon
                                    name={'StartTime'}
                                    style={{
                                        marginLeft: '6.2rem',
                                    }}
                                />
                            </Box>
                            <StartDateTimePicker
                                selectedStartDate={selectedStartDate}
                                setSelectedStartDate={handleStartDate}
                                disabled={!startDateEditable}
                                showWarningColor={
                                    isInvalidDate ||
                                    isDateInPast(selectedEndDate)
                                }
                            />
                        </Box>

                        <Box padding={'0 0.4rem'} display="flex">
                            <Box
                                style={{
                                    alignSelf: 'end',
                                    marginRight: '0.8rem',
                                }}
                            >
                                <Icon
                                    name={'EndTime'}
                                    style={{
                                        marginLeft: '6.2rem',
                                    }}
                                />
                            </Box>
                            <EndDateTimePicker
                                selectedEndDate={selectedEndDate}
                                setSelectedEndDate={handleEndDate}
                                disabled={!endDateEditable}
                                maxEndDate={
                                    policy?.isTimeConstrained
                                        ? moment(selectedStartDate).add(
                                              policy?.maximumValueInMinutes,
                                              'minute',
                                          )
                                        : null
                                }
                                showWarningColor={
                                    isInvalidDate ||
                                    isDateInPast(selectedEndDate)
                                }
                                // hasInvalidDates={hasInvalidDates}
                                // validationMessage={invalidDatesMessage}
                            />
                        </Box>
                    </Box>
                </>
            </Box>
            {policy && (
                <Box width={'60%'}>
                    <AccessRequestPolicyDetails
                        policy={policy}
                        orientation="vertical"
                        noOfColumns={2}
                    />
                </Box>
            )}
        </Box>
    )
}

export default DecisionTimeConstraintControlWrapper
